/**
 * VDB Framework — Vehicle Resolution Lookup
 * Build 003 / Commit 003.4
 */
(function(window, document) {
  "use strict";
  const VDB = window.VDB = window.VDB || {};

  function items(data, key) {
    return data?.items || data?.[key] || (Array.isArray(data) ? data : []);
  }

  function optionHtml(rows, placeholder) {
    return `<option value="">${placeholder}</option>` + rows.map(row => {
      const value = row.value ?? row.id ?? row.code ?? row.handle ?? row.name ?? row.label;
      const label = row.label ?? row.name ?? row.title ?? value;
      return `<option value="${VDB.utils.escapeHtml(value)}">${VDB.utils.escapeHtml(label)}</option>`;
    }).join("");
  }

  class Lookup {
    constructor(root) {
      this.root = root;
      this.make = root.querySelector("[data-vdb-make]");
      this.model = root.querySelector("[data-vdb-model]");
      this.year = root.querySelector("[data-vdb-year]");
      this.engine = root.querySelector("[data-vdb-engine]");
      this.submit = root.querySelector("[data-vdb-submit]");
      this.status = root.querySelector("[data-vdb-status]");
      this.lastResolved = null;

      if (!this.make || !this.model || !this.year || !this.engine) return;

      this.make.addEventListener("change", () => this.loadModels());
      this.model.addEventListener("change", () => this.loadYears());
      this.year.addEventListener("change", () => this.loadEngines());
      this.engine.addEventListener("change", () => this.updateSubmit());
      root.addEventListener("submit", event => this.save(event));
      this.loadMakes();
    }

    setBusy(value) {
      this.root.setAttribute("aria-busy", String(value));
      this.updateSubmit();
    }

    message(text, type = "") {
      if (!this.status) return;
      this.status.textContent = text;
      this.status.dataset.status = type;
    }

    reset(select, placeholder) {
      if (!select) return;
      select.innerHTML = `<option value="">${placeholder}</option>`;
      select.disabled = true;
    }

    updateSubmit() {
      if (!this.submit) return;
      const busy = this.root.getAttribute("aria-busy") === "true";
      this.submit.disabled = busy || !(
        this.make.value && this.model.value && this.year.value && this.engine.value
      );
    }

    async loadMakes() {
      try {
        this.setBusy(true);
        this.message("Loading vehicle makes…");
        const data = await VDB.api.getMakes();
        const rows = items(data, "makes");
        this.make.innerHTML = optionHtml(rows, "Select make");
        this.make.disabled = false;
        this.message(`${rows.length} vehicle makes loaded.`, "success");
      } catch (error) {
        this.message(error.message || "Vehicle makes could not be loaded.", "error");
      } finally {
        this.setBusy(false);
      }
    }

    async loadModels() {
      this.reset(this.model, "Select model");
      this.reset(this.year, "Select year");
      this.reset(this.engine, "Select engine");
      if (!this.make.value) return this.message("");

      try {
        this.setBusy(true);
        this.message("Loading vehicle models…");
        const data = await VDB.api.getModels(this.make.value);
        const rows = items(data, "models");
        this.model.innerHTML = optionHtml(rows, "Select model");
        this.model.disabled = rows.length === 0;
        this.message(rows.length ? `${rows.length} models loaded.` : "No active models found.", rows.length ? "success" : "error");
      } catch (error) {
        this.message(error.message || "Vehicle models could not be loaded.", "error");
      } finally {
        this.setBusy(false);
      }
    }

    async loadYears() {
      this.reset(this.year, "Select year");
      this.reset(this.engine, "Select engine");
      if (!this.model.value) return;

      try {
        this.setBusy(true);
        this.message("Loading production years…");
        const data = await VDB.api.getYears(this.make.value, this.model.value);
        const rows = items(data, "years").map(year => (
          typeof year === "object" ? year : { value: year, label: String(year) }
        ));
        this.year.innerHTML = optionHtml(rows, "Select year");
        this.year.disabled = rows.length === 0;
        this.message(rows.length ? `${rows.length} production years loaded.` : "No years found for this model.", rows.length ? "success" : "error");
      } catch (error) {
        this.message(`Year service: ${error.message}`, "error");
      } finally {
        this.setBusy(false);
      }
    }

    async loadEngines() {
      this.reset(this.engine, "Select engine");
      if (!this.year.value) return;

      try {
        this.setBusy(true);
        this.message("Loading engines…");
        const data = await VDB.api.getEngines(this.make.value, this.model.value, this.year.value);
        const rows = items(data, "engines");
        this.engine.innerHTML = optionHtml(rows, "Select engine");
        this.engine.disabled = rows.length === 0;
        this.message(rows.length ? `${rows.length} engine options loaded.` : "No engines found for this year.", rows.length ? "success" : "error");
      } catch (error) {
        this.message(`Engine service: ${error.message}`, "error");
      } finally {
        this.setBusy(false);
      }
    }

    async save(event) {
      event.preventDefault();
      if (this.submit?.disabled) return;

      try {
        this.setBusy(true);
        this.message("Resolving vehicle…");
        const result = await VDB.api.resolveVehicle({
          make: this.make.value,
          model: this.model.value,
          year: this.year.value,
          engine: this.engine.value
        });

        const vehicle = result.vehicle || result;
        this.lastResolved = vehicle;
        if (VDB.vehicle?.set) VDB.vehicle.set(vehicle);
        if (VDB.garage?.add) VDB.garage.add(vehicle);
        VDB.events?.emit?.("vehicle:resolved", vehicle);
        this.message(vehicle.displayName || vehicle.vehicleName || "Vehicle saved.", "success");
      } catch (error) {
        this.message(`Vehicle resolution: ${error.message}`, "error");
      } finally {
        this.setBusy(false);
      }
    }
  }

  function init(scope = document) {
    scope.querySelectorAll("[data-vdb-lookup]").forEach(element => {
      if (!element.vdbLookup) element.vdbLookup = new Lookup(element);
    });
  }

  VDB.ui = VDB.ui || {};
  VDB.ui.lookup = { init };
  document.addEventListener("DOMContentLoaded", () => init());
  document.addEventListener("shopify:section:load", event => init(event.target));
})(window, document);
